import { IJodit } from 'jodit/types/jodit'
import Consts from '../Const'
import { IEditorAttachment, IEditorNode } from '../Services/ServiceEditor'
import { DataResponseStatusIds, EmptyId, IDataResponse, mapAppTypeToServerAppType } from '../Services/ServiceTypes'
import Util from '../Util'
import { FolderNode } from './folders'
import { MutableRefObject } from 'react'

const SpeechRecognitionCtor = typeof window !== 'undefined' ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null
const hasSpeech = !!SpeechRecognitionCtor

const extensionIconMap: Record<string, string> = {
  pdf: 'woonotes-attachment-icon-pdf',

  doc: 'woonotes-attachment-icon-word',
  docx: 'woonotes-attachment-icon-word',

  xls: 'woonotes-attachment-icon-excel',
  xlsx: 'woonotes-attachment-icon-excel',

  png: 'woonotes-attachment-icon-image',
  jpg: 'woonotes-attachment-icon-image',
  jpeg: 'woonotes-attachment-icon-image',
  gif: 'woonotes-attachment-icon-image',
  svg: 'woonotes-attachment-icon-image',

  zip: 'woonotes-attachment-icon-zip',
  rar: 'woonotes-attachment-icon-zip',
  tar: 'woonotes-attachment-icon-zip',
  gz: 'woonotes-attachment-icon-zip',
  '7z': 'woonotes-attachment-icon-zip',

  txt: 'woonotes-attachment-icon-txt',

  mp3: 'woonotes-attachment-icon-sound',
  aac: 'woonotes-attachment-icon-sound',
  wav: 'woonotes-attachment-icon-sound',
  flac: 'woonotes-attachment-icon-sound',
  ogg: 'woonotes-attachment-icon-sound',
  m4a: 'woonotes-attachment-icon-sound',
  wma: 'woonotes-attachment-icon-sound',
  aiff: 'woonotes-attachment-icon-sound',
  aif: 'woonotes-attachment-icon-sound',

  avi: 'woonotes-attachment-icon-video',
  mov: 'woonotes-attachment-icon-video',
  mp4: 'woonotes-attachment-icon-video',
  mkv: 'woonotes-attachment-icon-video',
  wmv: 'woonotes-attachment-icon-video',
  m4v: 'woonotes-attachment-icon-video',
  mpeg: 'woonotes-attachment-icon-video',
  mpg: 'woonotes-attachment-icon-video',
}

function getIconClassByFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()
  if (!ext) return 'woonotes-attachment-icon-file'
  return extensionIconMap[ext] ?? 'woonotes-attachment-icon-file'
}

function getPositionByFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()

  switch (ext) {
    case 'pdf':
    case 'xsl':
    case 'xlsx':
    case 'docx':
    case 'avi':
    case 'mov':
    case 'mp4':
    case 'mkv':
    case 'wmv':
    case 'm4v':
    case 'mpeg':
    case 'mpg':
    case 'text':
    case 'zip':
    case 'rar':
    case 'tar':
    case 'gz':
    case '7z':
      return 'top:0px;left:0px'
    case 'mp3':
    case 'aac':
    case 'wav':
    case 'flac':
    case 'ogg':
    case 'm4a':
    case 'wma':
    case 'aiff':
    case 'aif':
      return 'top:3px;left:3px'
    default:
      return 'top:10px;left:10px'
  }
}

let uploadSource: 'image' | 'file' | undefined
const attachToolbarIntentListeners = (editor: any) => {
  const root: HTMLElement | null = editor?.container ?? null
  if (!root) return

  root.addEventListener(
    'click',
    e => {
      const el = e.target as HTMLElement | null
      if (!el) return

      const btn = el.closest('[data-ref]') as HTMLElement | null
      const name = btn?.getAttribute('data-ref')

      uploadSource = undefined
      if (name === 'image') uploadSource = 'image'
      if (name === 'file') uploadSource = 'file'
    },
    true, // capture hjælper hvis Jodit stopper propagation
  )
}

const attachPasteIntentListener = (editor: any) => {
  const editable: HTMLElement | null = editor?.editor ?? null // typisk contentEditable root
  if (!editable) return

  editable.addEventListener(
    'paste',
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return

      // Hvis der er et image i clipboard → det her paste er "image-flow"
      const hasImage = Array.from(items).some(it => it.kind === 'file' && it.type.startsWith('image/'))

      if (hasImage) {
        uploadSource = 'image'
        //setSource('image')
        //uploadSourceRef.current = 'paste'
      } else {
        // hvis det bare er tekst/html, lad den være unknown eller lad være med at ændre
        uploadSource = 'image'
        //setSource('image')
        //uploadSourceRef.current = 'unknown'
      }
    },
    true, // capture så vi kommer tidligt
  )
}

export const buildEditorConfig = (
  editorRef: MutableRefObject<any>,
  token: string,
  height: number,
  selectedNoneFolderNode: FolderNode | undefined,
  nodes: IEditorNode[] | undefined,
  submitting: boolean,
  setShowAttachmentsForNode: (selectedNoneFolderNodeId: string | undefined) => void,
  setShowHistoryForNode: (selectedNoneFolderNodeId: string | undefined) => void,
  doTryToggleLock: () => void,
  setErrorMsg: (errorMsg: string) => void,
  doSave: () => void,
) => {
  const baseUploader = {
    insertImageAsBase64URI: Consts.isOtherDomainThanAPI,
    url: `${Consts.defaultBaseUrl}/editor/addEditorAttachments`,
    method: 'POST',
    format: 'json',
    headers: {
      AuthorizationDeviceId: 'Editor',
      AuthorizationToken: token,
      NodeId: selectedNoneFolderNode?.id ?? EmptyId,
      AppType: `${mapAppTypeToServerAppType('editor')}`,
    },
    isSuccess: (resp: any) => !resp.error,
    process: (resp: any) => {
      const dataResponse = resp as IDataResponse

      if (dataResponse.Status.Id === DataResponseStatusIds.General_OK) {
        return {
          status: dataResponse.Status.Id,
          editorAttachments: dataResponse.Data as IEditorAttachment[],
        }
      }

      return { status: dataResponse.Status.Id, editorAttachments: undefined }
    },
  }

  const selectedIsLocked = nodes?.find(n => n.Id === selectedNoneFolderNode?.id)?.Locked ?? false
  //console.log('CREATE NEW BUTTONS!!!!!!!!!!!!!!!!!!!!!', selectedNoneFolderNode?.isLocked ? 'unlock' : 'lock')

  const buttonForUnlocked = [
    '|',
    //...xxx, //(Jodit as any).defaultOptions.buttons,
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'eraser',
    '|',
    'ul',
    'ol',
    '|',
    'font',
    'fontsize',
    'paragraph',
    'lineHeight',
    '---',
    'superscript',
    'subscript',
    'classSpan',
    '|',
    'image',
    'file',
    'video',
    {
      name: 'showAttachments',
      tooltip: 'Show attachments',
      //iconURL: 'https://www.woohooo.dk/static/media/wwwrankytank30pct.ee13d48a25b21deb9e57.png',
      icon: 'showAttachments',
      popup: (jodit: any) => {
        //const html = jodit.value
        setShowAttachmentsForNode(selectedNoneFolderNode?.id)
        //onLogout?.()

        // const div = jodit.create.element('div')
        // div.style.padding = '12px'
        // div.style.maxWidth = '240px'
        // div.textContent = 'Hello world'

        // // eksempel: klik inde i popup -> indsæt tekst i editor
        // div.addEventListener('click', () => {
        //   jodit.s.insertHTML('<strong>Indsat fra popup</strong>')
        //   jodit.events?.fire?.('closeAllPopups') // luk popup (virker i mange builds)
        // })

        // return div
      },
    },
    {
      name: 'showHistory',
      tooltip: 'Show history',
      //iconURL: 'https://www.woohooo.dk/static/media/wwwrankytank30pct.ee13d48a25b21deb9e57.png',
      icon: 'showHistory',
      popup: (jodit: any) => {
        setShowHistoryForNode(selectedNoneFolderNode?.id)
      },
    },
    '\n',
    'spellcheck',
    ...(hasSpeech ? ['speechRecognize', '|'] : []),
    '|',
    'cut',
    'copy',
    'paste',
    'selectAll',
    'copyformat',
    '|',
    'table',
    'hr',
    'link',
    'symbols',
    '|',
    'indent',
    'outdent',
    'left',
    '|',
    'brush',
    '---',
    'undo',
    'redo',
    '|',
    'find',
    '|',
    'source',
    '|',
    'fullsize',
    'preview',
    'print',
    'save',
  ]

  return {
    readonly: submitting || selectedIsLocked,
    link: {
      preventReadOnlyNavigation: false,
    },
    placeholder: '',
    speechRecognize: {
      api: SpeechRecognitionCtor,
      lang: 'en-US',
      continuous: false,
    },
    activeButtonsInReadOnly: submitting ? ['print', 'fullsize', 'source'] : [selectedIsLocked ? 'unlock' : 'lock', 'print', 'fullsize', 'source'],
    buttons: [
      {
        name: selectedIsLocked ? 'unlock' : 'lock',
        tooltip: selectedIsLocked ? 'Unlock content' : 'Lock content',
        //iconURL: 'https://www.woohooo.dk/static/media/wwwrankytank30pct.ee13d48a25b21deb9e57.png',
        icon: selectedIsLocked ? 'unlock' : 'lock',
        popup: (jodit: any) => {
          // const html = jodit.value
          // console.log('LOCK:', html)
          doTryToggleLock()
          //setShowAttachmentsForNode(selectedNoneFolderNode?.id)
          //onLogout?.()

          // const div = jodit.create.element('div')
          // div.style.padding = '12px'
          // div.style.maxWidth = '240px'
          // div.textContent = 'Hello world'

          // // eksempel: klik inde i popup -> indsæt tekst i editor
          // div.addEventListener('click', () => {
          //   jodit.s.insertHTML('<strong>Indsat fra popup</strong>')
          //   jodit.events?.fire?.('closeAllPopups') // luk popup (virker i mange builds)
          // })

          // return div
        },
      },
      ...(selectedIsLocked ? [] : buttonForUnlocked),
    ],
    // controls: {
    //   save: {
    //     isActive: () => false,
    //     exec: (editor: any) => {
    //       //handleSave()
    //     },
    //   },
    // },
    height: height,
    allowResizeY: false,
    allowResizeX: false,
    //width: 1600,
    // exportDocs: {
    //   ajax: {
    //     url: `${Consts.defaultBaseUrl}/editor/addEditorAttachments`,
    //   },
    // },
    prepareData: (formData: FormData) => {
      // send med til API (så backend kan gemme det hvis du vil)
      formData.append('uploadSource', uploadSource ?? 'unknown')
      return formData
    },
    uploader: {
      ...baseUploader,
      defaultHandlerSuccess: function (
        this: any,
        data: {
          baseurl: string
          files: string[]
          isImages: boolean[]
          status: DataResponseStatusIds | undefined
          editorAttachments: IEditorAttachment[] | undefined
        },
      ) {
        //data: { baseurl: '', files: [], isImages: [true]}
        console.log('uploader.defaultHandlerSuccess...')
        console.log(data)

        if (data.files?.length > 0 && data.isImages?.length > 0) // which means insertImageAsBase64URI=true
        {
          for (let i = 0; i < data.isImages.length; i++) {
            if (!data.isImages[i]) continue

            const base64Source = data.files[i]
            editorRef.current.selection.insertHTML(`<img src="${base64Source}" />`)
          }
          return
        }

        // const ids: string[] = data.files || []
        // for (const id of ids) {
        //   const url = `${Consts.defaultBaseUrl}/editor/getEditorAttachment?id=${id}`
        //   this.selection.insertHTML(`<a href="${url}" target="_blank" rel="noopener">Download fil</a>`)
        // }

        if ((data.status !== undefined && data.status !== DataResponseStatusIds.General_OK) || !data.editorAttachments) {
          setErrorMsg(`Failed to upload file(s) (${data.status})`)
          return
        }

        for (const editorAttachment of data.editorAttachments) {
          //const url = `[[[BASEURL]]]/editor/getEditorAttachmentData?editorAttachmentId=${editorAttachment.Id}&authorizationDeviceId=Editor&authorizationToken=[[[TOKEN]]]&nodeId=${editorAttachment.NodeId}&appType=${mapAppTypeToServerAppType('editor')}`
          //this.selection.insertHTML(`<a href="#" data-file-id="${EmptyId}">Download fil</a>`) til preview... senere!

          if (uploadSource === 'image') {
            const url = `${Consts.defaultBaseUrl}/editor/getEditorAttachmentData?hmm=1337&editorAttachmentId=${editorAttachment.Id}&nodeId=${editorAttachment.NodeId}&appType=${mapAppTypeToServerAppType('editor')}&useCookie=true`
            editorRef.current.selection.insertHTML(`<img src="${url}" alt="${Util.htmlEncode(editorAttachment.Filename)}" />`)
          } else {
            const url = `${Consts.defaultBaseUrl}/editor/getEditorAttachmentData?hmm=1338&editorAttachmentId=${editorAttachment.Id}&nodeId=${editorAttachment.NodeId}&appType=${mapAppTypeToServerAppType('editor')}&useCookie=true`
            //editorRef.current.selection.insertHTML(`<a href="${url}" target="_blank" rel="noopener">${htmlEncode(editorAttachment.Filename)}</a>`)

            //   <div style="position:relative;width:20px;height:20px;padding: 20px;">

            //     <div style="position:absolute;top:10px;left:10px" class="woonotes-attachment-icon-file"><br></div>
            // </div>

            editorRef.current.selection.insertHTML(
              `<div>
                    <a class="woonotes-attachment-link" href="${url}" target="_blank" rel="noopener">
                        <div class="woonotes-attachment-container">
                            <div style="position:relative;width:20px;height:20px;padding: 20px;">
                              <div style="position:absolute;${getPositionByFilename(editorAttachment.Filename)}" class="${getIconClassByFilename(editorAttachment.Filename)}"></div>
                            </div>
                            <div class="woonotes-attachment-title">${Util.htmlEncode(editorAttachment.Filename)}</div>
                            <div class="woonotes-attachment-size">${Util.htmlEncode(Util.formatBytesSI(editorAttachment.Size))}</div>
                        </div>
                    </a>
                </div>
                <br>`,
            )
          }
          //this.selection.insertHTML(`<a href="${url}" target="_blank" rel="noopener">Download ${htmlEncode(data.filename)}</a>`)
        }
      },
    },
    style: {
      //top: -5,
      //paddingTop: 30,
      //padding: 10,
    },
    // cleanHTML: {
    //   cleanOnPaste: false,
    // },
    allowTags: {
      ol: {
        type: true,
        style: true,
      },
    },
    hotkeys: {
      'ctrl+l': selectedIsLocked ? 'unlock' : 'lock',
    },
    events: {
      keydown: (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l') {
          doTryToggleLock()
          e.preventDefault()
          e.stopPropagation()
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
          e.preventDefault()
          doSave()
        }
      },
      afterInit: (editor: IJodit) => {
        editorRef.current = editor

        //#region Source
        attachToolbarIntentListeners(editor)
        attachPasteIntentListener(editor)
        //#endregion Source

        // console.log('after init called')
        // const isReadonly = editor.getReadOnly()
        // editor.setReadOnly(!isReadonly)
        //editorRef.current

        //prime(editor);
      },
    },
    //   events: {
    //     command: (cmd: string, jodit: any) => {
    //       console.log(`cmd = ${cmd}`)
    //       if (cmd === 'save') {
    //         const html = jodit.value

    //         console.log('SAVE:', html)

    //         // din save logik her
    //       }
    //     },
    //   },
  }
}
